import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getTemplates,
  getTemplate,
  createTemplate,
  extractVariables,
  updateTemplateVariable,
  generateNegotiation,
  contextFill,
} from '../services/templatesApi'
import type {
  TemplateListResponse,
  TemplateDetail,
  TemplateVariable,
  TemplateSection,
  CreateTemplateResponse,
  ExtractVariablesResponse,
  TemplateCategory,
  TemplatePurpose,
  VariableFieldType,
  GenerateNegotiationRequest,
  GenerateNegotiationResponse,
  ContextFillRequest,
  ContextFillResponse,
} from '../services/templatesApi'

/** Fetch paginated template list, filtered by organization and purpose */
export function useTemplateList(params?: {
  search?: string
  category?: string
  page?: number
  organizationId?: string
  purpose?: TemplatePurpose
  enabled?: boolean
}) {
  return useQuery<TemplateListResponse>({
    queryKey: ['templates', params?.search, params?.category, params?.page, params?.organizationId, params?.purpose],
    queryFn: () => getTemplates({
      search: params?.search,
      category: params?.category,
      page: params?.page,
      organizationId: params?.organizationId,
      purpose: params?.purpose,
    }),
    enabled: params?.enabled ?? true,
    staleTime: 30_000,
  })
}

/** Fetch a single template with full details + variables */
export function useTemplate(id: number | null, organizationId?: string) {
  return useQuery<TemplateDetail>({
    queryKey: ['template', id, organizationId],
    queryFn: () => getTemplate(id!, organizationId),
    enabled: id !== null,
    staleTime: 60_000,
  })
}

/**
 * Create a template from the current Word document.
 * Uploads DOCX, then auto-extracts {{variable}} placeholders.
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      file: Blob
      filename: string
      name: string
      description?: string
      category?: TemplateCategory
      organizationId?: string
    }): Promise<{ template: CreateTemplateResponse; variables: ExtractVariablesResponse }> => {
      // Step 1: Create the template
      const template = await createTemplate(params)

      // Step 2: Auto-extract variables
      const variables = await extractVariables(template.id, params.organizationId)

      return { template, variables }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

/** Update a single template variable's metadata (label, field_type, is_required) */
export function useUpdateTemplateVariable() {
  return useMutation({
    mutationFn: (params: {
      templateId: number
      variableId: number
      data: Partial<Pick<TemplateVariable, 'label' | 'field_type' | 'is_required' | 'description' | 'default_value' | 'order' | 'group'>>
      organizationId?: string
    }) => updateTemplateVariable(params.templateId, params.variableId, params.data, params.organizationId),
  })
}

/**
 * Convenience accessor for template sections.
 * Sections come embedded in the template detail response,
 * but this hook extracts them for easier consumption.
 */
export function useSections(templateId: number | null, organizationId?: string) {
  const query = useTemplate(templateId, organizationId)

  return {
    ...query,
    data: query.data?.sections ?? [],
  } as Omit<typeof query, 'data'> & { data: TemplateSection[] }
}

/**
 * AI-powered context fill — suggests variable values
 * based on party information and additional context.
 */
export function useContextFill(templateId: string, organizationId?: string) {
  return useMutation<ContextFillResponse, Error, ContextFillRequest>({
    mutationFn: (data: ContextFillRequest) =>
      contextFill(templateId, data, organizationId),
  })
}

/**
 * Generate a negotiation from a unified template.
 * Creates the negotiation with filled variables and selected sections.
 */
export function useGenerateNegotiation(templateId: string, organizationId?: string) {
  const queryClient = useQueryClient()

  return useMutation<GenerateNegotiationResponse, Error, GenerateNegotiationRequest>({
    mutationFn: (data: GenerateNegotiationRequest) =>
      generateNegotiation(templateId, data, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['negotiations'] })
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

/**
 * Batch-update multiple template variables sequentially.
 * Used by the template creation wizard after extract-variables.
 */
export async function batchUpdateVariables(
  templateId: number,
  variables: TemplateVariable[],
  configs: Record<string, { label: string; field_type: VariableFieldType; is_required: boolean }>,
  organizationId?: string,
): Promise<void> {
  for (const variable of variables) {
    const config = configs[variable.name]
    if (!config) continue

    await updateTemplateVariable(templateId, variable.id, {
      label: config.label,
      field_type: config.field_type,
      is_required: config.is_required,
    }, organizationId)
  }
}
